// Clientes del Portal Admin VentasLuga: filtros · tabla · cajón del cliente.
// UNA llamada por página llena la tabla; fechas, tipo de cliente, tienda y vendedor se filtran en el
// servidor, y la búsqueda, el nivel, el rango, las píldoras y el orden, sobre lo cargado.
// El cliente seleccionado (`?cliente=`) y la pestaña del cajón (`?tab=`) viven en la URL.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { CUSTOMERS_NOUN, loadedFiltersNote } from '@/shared/patterns/LoadedScope';
import { useRosterRetailer, useRosterStores, useRosterUsers } from '../lib/useRoster';
import { useCustomerDetail, useCustomerPurchases, useCustomersTable } from './api/useCustomers';
import { CustomersFiltersPanel } from './components/CustomersFiltersPanel';
import { CustomersTable } from './components/CustomersTable';
import {
    CustomerDetailPanel,
    parseDetailTab,
    type DetailTab,
} from './components/CustomerDetailPanel';
import {
    bucketCounts,
    DEFAULT_SORT,
    EMPTY_FILTERS,
    filterCustomers,
    sellerOptions,
    serverFiltersOf,
    sortCustomers,
    storeOptions,
    withOriginStoreNames,
    type CustomersFilters,
    type SortState,
} from './lib/customersList';

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

export function ClientesPage() {
    const navigate = useNavigate();
    const { cliente, tab: rawTab } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const tab = parseDetailTab(rawTab);

    const [filters, setFilters] = useState<CustomersFilters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // Memo por valor: los filtros de servidor solo cambian cuando cambia alguno de sus campos.
    const { from, to, storeId, sellerId, customerType } = serverFiltersOf(filters);
    const serverFilters = useMemo(
        () => ({ from, to, storeId, sellerId, customerType }),
        [from, to, storeId, sellerId, customerType],
    );
    const list = useCustomersTable(serverFilters);
    const detail = useCustomerDetail(cliente ?? null);
    const purchases = useCustomerPurchases(cliente ?? null);

    const { retailerId } = useRosterRetailer();
    const rosterStores = useRosterStores(retailerId);
    const rosterUsers = useRosterUsers(retailerId);

    const stores = useMemo(() => storeOptions(rosterStores.data ?? []), [rosterStores.data]);
    const storeNames = useMemo(
        () => new Map(stores.map((store) => [store.id, store.name])),
        [stores],
    );
    const rows = useMemo(
        () => withOriginStoreNames(list.rows, storeNames),
        [list.rows, storeNames],
    );
    const counts = useMemo(
        // Con «Tipo de cliente» puesto, los counts traen a quien no se cargó: se cuenta lo cargado.
        () =>
            bucketCounts(
                rows,
                list.hasAggregates,
                list.creditState,
                filters.customerType === 'ALL' ? list.accountOnlyCount : null,
            ),
        [rows, list.hasAggregates, list.creditState, list.accountOnlyCount, filters.customerType],
    );
    const sellers = useMemo(
        () => sellerOptions(rosterUsers.data ?? [], filters.storeId),
        [rosterUsers.data, filters.storeId],
    );
    const visibleRows = useMemo(
        () => sortCustomers(filterCustomers(rows, filters), sort),
        [rows, filters, sort],
    );
    const selectedRow = rows.find((row) => row.retailCustomerId === cliente);

    const selectCustomer = (retailCustomerId: string | null, nextTab?: DetailTab) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'clientes' },
            search: { cliente: retailCustomerId ?? undefined, tab: nextTab ?? tab },
            replace: true,
        });
    };

    /** El último id que la tabla llegó a mostrar seleccionado. */
    const shownSelectionRef = useRef<string | null>(null);

    // Sin `?cliente=` abre la primera fila; si la abierta sale del filtro, salta a la primera
    // visible o limpia la selección. Un `?cliente=` recién llegado por link se respeta.
    useEffect(() => {
        if (!list.isSuccess) return;
        if (!cliente) {
            if (visibleRows.length > 0) selectCustomer(visibleRows[0].retailCustomerId);
            return;
        }
        if (visibleRows.some((r) => r.retailCustomerId === cliente)) {
            shownSelectionRef.current = cliente;
            return;
        }
        if (shownSelectionRef.current !== cliente) return;
        shownSelectionRef.current = null;
        selectCustomer(visibleRows.length > 0 ? visibleRows[0].retailCustomerId : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectCustomer se re-crea en cada render
    }, [cliente, visibleRows, list.isSuccess]);

    // `refetch()` ignora el `enabled`: sin selección no se piden la ficha ni el historial.
    const retryAll = () => {
        list.refetch();
        if (cliente) {
            void detail.refetch();
            purchases.refetch();
        }
    };

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                filters={
                    <CustomersFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        stores={stores}
                        sellers={sellers}
                        creditState={list.creditState}
                        hasAggregates={list.hasAggregates}
                        loadedNote={loadedFiltersNote(
                            { loaded: rows.length, hasMore: list.hasNextPage },
                            CUSTOMERS_NOUN,
                        )}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <CustomersTable
                        rows={visibleRows}
                        loadedCount={rows.length}
                        counts={counts}
                        bucket={filters.bucket}
                        onBucketChange={(bucket) => {
                            setFilters({ ...filters, bucket });
                        }}
                        windowFrom={list.windowFrom}
                        truncated={list.truncated}
                        aggregatesUnavailableReason={list.aggregatesUnavailableReason}
                        creditState={list.creditState}
                        sort={sort}
                        onSortChange={setSort}
                        selectedId={cliente ?? null}
                        onSelect={(id) => {
                            selectCustomer(id);
                        }}
                        isLoading={list.isPending}
                        error={asError(list.error)}
                        onRetry={retryAll}
                        hasNextPage={list.hasNextPage}
                        isFetchingNextPage={list.isFetchingNextPage}
                        onLoadMore={list.fetchNextPage}
                        nextPageError={list.nextPageError}
                    />
                }
                detail={
                    <CustomerDetailPanel
                        customer={detail.data}
                        purchases={purchases.purchases}
                        isLoadingMorePurchases={purchases.isLoadingMore}
                        purchasesTruncated={purchases.isTruncated}
                        credit={selectedRow?.credit}
                        accountOnly={selectedRow?.accountOnly === true}
                        creditState={list.creditState}
                        tab={tab}
                        onTabChange={(nextTab) => {
                            selectCustomer(cliente ?? null, nextTab);
                        }}
                        isLoading={detail.isPending}
                        isLoadingPurchases={purchases.isPending}
                        error={asError(detail.error)}
                        purchasesError={asError(purchases.error)}
                        onRetry={retryAll}
                        hasSelection={Boolean(cliente)}
                    />
                }
            />
        </ScreenFrame>
    );
}
